import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { postJSON } from "../hooks/useMetals";
import { useLang } from "../i18n";

const SLOTS = [
  { value: "Morning 9–12", label: { en: "Morning 9–12", it: "Mattina 9–12" } },
  { value: "Afternoon 12–17", label: { en: "Afternoon 12–17", it: "Pomeriggio 12–17" } },
  { value: "Evening 17–19", label: { en: "Evening 17–19", it: "Sera 17–19" } },
];
const TYPES = [
  { value: "atelier", label: { en: "At the atelier", it: "In atelier" } },
  { value: "video", label: { en: "Video call", it: "Videochiamata" } },
];

export default function Booking() {
  const { L } = useLang();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "", date: "", slot: SLOTS[0].value, type: "atelier" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postJSON("/bookings", {
        name: form.name, email: form.email, date: form.date,
        time_slot: form.slot, consultation_type: form.type, notes: form.message,
      });
      toast.success(L({ en: "Consultation requested — we'll confirm your slot by email.", it: "Consulenza richiesta — confermeremo lo slot via email." }));
      setForm({ name: "", email: "", message: "", date: "", slot: SLOTS[0].value, type: "atelier" });
    } catch {
      toast.error(L({ en: "Something went wrong sending your request. Please try again.", it: "Errore nell'invio della richiesta. Riprova." }));
    } finally {
      setBusy(false);
    }
  };

  const pill = (active) =>
    `rounded-full border px-4 py-2 text-xs transition-all duration-300 ${
      active ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#8A6D2B]" : "border-[#0E3B33]/15 text-[#4A5248] hover:border-[#C9A84C]/60"
    }`;

  return (
    <section id="book" className="border-t hairline bg-[#F2EDE3] py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="mono-tech mb-5 text-[#A8853B]">{L({ en: "Begin a Commission", it: "Inizia una Commissione" })}</p>
          <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0E3B33] sm:text-5xl">
            {L({ en: "Bring the idea.", it: "Porta l'idea." })} <br />
            <span className="gold-text italic">{L({ en: "We'll bring the fire.", it: "Al fuoco pensiamo noi." })}</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5248]">
            {L({
              en: "Book a consultation at the bench — in person at the atelier or over video. Every enquiry is answered by the goldsmith, never a bot.",
              it: "Prenota una consulenza al banco — di persona in atelier o in videochiamata. Ogni richiesta riceve risposta dall'orafo, mai da un bot.",
            })}
          </p>
          <Link to="/contact" data-testid="booking-quote-link" className="group mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#A8853B] transition-colors hover:text-[#0E3B33]">
            {L({ en: "Need a written quote instead? Start a project", it: "Serve un preventivo scritto? Inizia un progetto" })}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <div className="mt-10 space-y-4 border-t hairline pt-8 text-sm text-[#4A5248]">
            <div data-testid="contact-atelier"><span className="mono-tech mr-3 text-[#8A8F82]">{L({ en: "Atelier", it: "Atelier" })}</span> Urbino, 61029 PU, Italy</div>
            <div data-testid="contact-email"><span className="mono-tech mr-3 text-[#8A8F82]">Email</span> daniela@da-creations.com</div>
            <div data-testid="contact-phone"><span className="mono-tech mr-3 text-[#8A8F82]">{L({ en: "Phone", it: "Telefono" })}</span> +39 371 615 9381</div>
            <div data-testid="contact-hours"><span className="mono-tech mr-3 text-[#8A8F82]">{L({ en: "Hours", it: "Orari" })}</span> {L({ en: "Mon–Fri 9:00–18:00 · Sat by appointment", it: "Lun–Ven 9:00–18:00 · Sab su appuntamento" })}</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="rounded-xl border border-[#C9A84C]/40 bg-white/90 p-7 shadow-xl backdrop-blur-xl sm:p-9">
            <p className="mono-tech mb-6 text-[#A8853B]" data-testid="booking-form-title">{L({ en: "Book a Consultation", it: "Prenota una Consulenza" })}</p>
            <form onSubmit={submit} className="space-y-5" data-testid="consultation-booking-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <input required data-testid="booking-name-input" placeholder={L({ en: "Your name", it: "Il tuo nome" })} value={form.name} onChange={set("name")} className="forge-field" />
                <input required type="email" data-testid="booking-email-input" placeholder={L({ en: "Email address", it: "Indirizzo email" })} value={form.email} onChange={set("email")} className="forge-field" />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <input required type="date" data-testid="booking-date-input" value={form.date} onChange={set("date")} className="forge-field" min={new Date().toISOString().slice(0, 10)} />
                <div className="flex flex-wrap gap-2">
                  {SLOTS.map((s) => (
                    <button
                      type="button" key={s.value}
                      data-testid={`slot-${s.value.split(" ")[0].toLowerCase()}`}
                      onClick={() => set("slot")(s.value)}
                      className={pill(form.slot === s.value)}
                    >
                      {L(s.label)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button
                    type="button" key={t.value}
                    data-testid={`consult-type-${t.value}`}
                    onClick={() => set("type")(t.value)}
                    className={pill(form.type === t.value)}
                  >
                    {L(t.label)}
                  </button>
                ))}
              </div>
              <textarea
                required rows={5}
                data-testid="booking-message-input"
                placeholder={L({ en: "Anything we should prepare for the consultation?", it: "C'è qualcosa che dovremmo preparare per la consulenza?" })}
                value={form.message}
                onChange={set("message")}
                className="forge-field resize-none"
              />
              <button type="submit" disabled={busy} data-testid="booking-submit-btn" className="ember-btn w-full justify-center disabled:opacity-60">
                {busy ? L({ en: "Sending…", it: "Invio…" }) : L({ en: "Request Consultation", it: "Richiedi Consulenza" })}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
